import React from 'react'

import Section from '../Section'
import Header, { Title } from '../Header'
import Separator from '../Separator'
import { Flex, TruncateWithTooltip } from '@sketch/components'

import { SymbolLink, SymbolUnavailable } from '../../../Symbol'
import { Chevron } from '../TextOrLayerStyles'

import { getArtboardThumbnail } from 'modules/shares/utils/getArtboardThumbnail'

import {
  GetArtboardInDocumentWithUuidQuery,
  useGetDetailViewArtboardQuery,
} from '@sketch/gql-types'
import { useVersioning } from 'modules/versioning'
import {
  StyledSkeleton,
  SymbolBox,
  SymbolContainer,
  SymbolPath,
  SymbolThumbnail,
} from './SymbolInSidebar.styles'
import { useFlag } from '@sketch/modules-common'
import { useOnEvent } from '@sketch/utils'
import { useComponentsState } from 'modules/shares/components/ComponentsStateContext'

type SymbolArtboard = OmitSafe<
  NonNullable<GetArtboardInDocumentWithUuidQuery['artboardInDocumentWithUuid']>,
  '__typename'
>

interface SymbolProps {
  name: string
  symbolArtboard: SymbolArtboard | null
  shareIdentifier: string
  path?: string
  isForeign?: boolean
  foreignVersionShortId?: string
  permanentArtboardShortId?: string
}

const SymbolInSidebar: React.FC<SymbolProps> = ({
  name,
  symbolArtboard,
  shareIdentifier,
  path,
  isForeign,
  foreignVersionShortId,
  permanentArtboardShortId,
}) => {
  // TODO: Remove FF when released
  // https://github.com/sketch-hq/Cloud/issues/17734
  const isComponentsOnDemandOn = useFlag('component-ingest-on-demand')

  const versionContext = useVersioning()

  const { data, refetch } = useGetDetailViewArtboardQuery({
    shouldInvalidatePrevious: (prev, curr) =>
      prev?.permanentArtboardShortId !== curr?.permanentArtboardShortId,
    // To account for artboard updates we're setting a cache-and-network
    // policy for artboard queries without an explicit version.
    fetchPolicy: versionContext.versionShortId
      ? 'cache-first'
      : 'cache-and-network',
    variables: {
      permanentArtboardShortId: symbolArtboard?.permanentArtboardShortId ?? '',
      documentVersionShortId:
        foreignVersionShortId ?? versionContext.versionShortId,
      shareIdentifier,
    },
    skip:
      !versionContext.versionShortId ||
      !shareIdentifier ||
      !symbolArtboard?.permanentArtboardShortId,
  })

  useOnEvent('componentsAreReady', () => {
    refetch()
  })

  const symbolThumbnail = data?.artboard
    ? getArtboardThumbnail(data.artboard, 'S')
    : null

  const isSymbolText =
    !symbolArtboard?.isSymbolMaster ||
    !symbolArtboard?.permanentArtboardShortId ||
    // if we're at the last level of the symbol tree, we don't want to show the link to nowhere
    permanentArtboardShortId === symbolArtboard?.permanentArtboardShortId

  const symbolDetail = (
    <SymbolBox isClickable={!isSymbolText}>
      {symbolThumbnail && (
        <Flex width="32px" marginRight={2}>
          <SymbolThumbnail src={symbolThumbnail} alt="thumbnail" />
        </Flex>
      )}
      <SymbolContainer
        marginRight={2}
        data-testid="inspector-sidebar-symbol-info"
      >
        <TruncateWithTooltip>{name}</TruncateWithTooltip>
        <SymbolPath data-testid="inspector-sidebar-symbol-path">
          <TruncateWithTooltip>{path} </TruncateWithTooltip>
        </SymbolPath>
      </SymbolContainer>
      {!isSymbolText ? <Chevron /> : null}
    </SymbolBox>
  )

  const componentsState = useComponentsState()

  const isIngesting = componentsState === 'PROCESSING'

  return (
    <>
      <Separator />
      <Section data-testid="inspector-sidebar-symbol">
        <Header>
          <Title>Symbol</Title>
        </Header>
        {isComponentsOnDemandOn && isIngesting ? (
          <StyledSkeleton />
        ) : isSymbolText ? (
          <SymbolUnavailable>{symbolDetail}</SymbolUnavailable>
        ) : (
          <SymbolLink
            isForeign={!!isForeign}
            isSamePermanentArtboardShortId={
              permanentArtboardShortId ===
              symbolArtboard?.permanentArtboardShortId
            }
            symbolArtboard={symbolArtboard}
            shareIdentifier={shareIdentifier}
          >
            {symbolDetail}
          </SymbolLink>
        )}
      </Section>
    </>
  )
}

export { SymbolInSidebar }
